import { useMemo } from "react";
import { calculateTextWidth } from "../../utilities/text";
import classNames from "classnames";
import { NormalizationErrorType } from "../../../annotation/interfaces/annotation";

const DOUBLE_COLUMNS_WIDTH = 150;
const SINGLE_COLUMN_WIDTH = 320;

interface UseEntityTextVisualProps {
  value: string;
  normalizedValue: string | undefined;
  showLabels?: boolean;
  showNormalizedValue?: boolean;
  requiresAttention?: boolean;
  errorType?: NormalizationErrorType;
  cellClasses?: string;
}

const useEntityTextVisual = ({
  value,
  normalizedValue,
  showLabels = true,
  showNormalizedValue = true,
  requiresAttention = false,
  errorType,
  cellClasses = "",
}: UseEntityTextVisualProps) => {
  const textValue = useMemo(
    () => normalizedValue ?? value,
    [value, normalizedValue],
  );

  const textWidthValue = useMemo(
    () => calculateTextWidth(value || ""),
    [value],
  );

  const textWidthNormalizedValue = useMemo(
    () => calculateTextWidth(normalizedValue || ""),
    [normalizedValue],
  );

  const showValueTooltip = textWidthValue > DOUBLE_COLUMNS_WIDTH;
  const showNormalizedValueTooltip =
    textWidthNormalizedValue > DOUBLE_COLUMNS_WIDTH;

  const hasInvalidNormalizedValue = useMemo(() => {
    return (
      errorType === NormalizationErrorType.INVALID_VALUE ||
      errorType === NormalizationErrorType.ERROR_WHILE_NORMALIZING
    );
  }, [errorType]);

  const normalizedClassName = useMemo(
    () =>
      classNames({
        "entity-visual__element": true,
        invalid: hasInvalidNormalizedValue,
        "requires-attention": requiresAttention,
      }),
    [hasInvalidNormalizedValue, requiresAttention],
  );

  const labeledClassName = useMemo(
    () =>
      classNames({
        "entity-visual__element": true,
        "requires-attention": hasInvalidNormalizedValue
          ? false
          : requiresAttention,
      }),
    [hasInvalidNormalizedValue, requiresAttention],
  );

  const labeledWithoutNormalizedClassName = useMemo(
    () =>
      classNames({
        "entity-visual__element_labeled": true,
        "requires-attention": requiresAttention,
      }),
    [requiresAttention],
  );

  const singleColumnTooltip = useMemo(() => {
    const showTooltip = textWidthValue > SINGLE_COLUMN_WIDTH;
    return showTooltip ? value : null;
  }, [textWidthValue, value]);

  // Fallback logic (when value or normalizedValue is missing)
  const fallbackData = useMemo(() => {
    const tooltipValue = normalizedValue ?? value;
    const showTooltip = normalizedValue
      ? textWidthNormalizedValue > SINGLE_COLUMN_WIDTH
      : textWidthValue > SINGLE_COLUMN_WIDTH;
    const tooltip = showTooltip ? tooltipValue : null;

    return { tooltipValue, tooltip };
  }, [normalizedValue, value, textWidthNormalizedValue, textWidthValue]);

  return {
    textValue,
    cellClasses,
    showValueTooltip,
    showNormalizedValueTooltip,
    hasInvalidNormalizedValue,
    normalizedClassName,
    labeledClassName,
    labeledWithoutNormalizedClassName,
    singleColumnTooltip,
    fallbackData,
    showLabels,
    showNormalizedValue,
  };
};

export default useEntityTextVisual;
